import React, { useEffect, useState } from 'react'
import { useText } from '../context/TextContext'
import { ORGANIZATION_SETTINGS_TABS, QUERY_PARAMS, STORAGE_PATHS } from '../utils/shared/constants';
import { BillingTab } from '../components/BillingTab';
import { AdvancedButton } from '../components/AdvancedButton';
import { useOrganization } from '../context/OrganizationContext';
import { useUser } from '../context/UserContext';
import AddressSearch from '../components/AddressSearch';
import OrganizationLogo from '../components/OrganizationLogo';
import TeamManagement from '../components/TeamManagement';
import OrgMemberSelect from '../components/OrgMemberSelect';
import { Organization } from '../models/shared/Organization';
import { UserRole } from '../models/shared/UserInvitation';
import { OrgCurrency } from '../utils/shared/constants';
import { toast } from 'react-toastify';
import { useStorage } from 'reactfire';
import { ref, getDownloadURL, uploadBytes } from 'firebase/storage';
import useQueryParam, { parseQueryParam } from '../hooks/useQueryParams';
import { useNavigate, useLocation } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { FormControl, Tabs, Tab, Form, FormCheck, FormSelect, Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import useConfirmModal from '../hooks/useConfirmModal';
import config from '../utils/config';
import { createOrganization as createOrganizationApi, deleteOrganization as deleteOrganizationApi, transferOwnership as transferOwnershipApi } from '../utils/api';



export default function OrganizationSettingsPage() {
    const [warnAboutMissingName, setWarnAboutMissingName] = useState(false);
    const ORGANIZATION_NAME_INPUT_ID = 'organization-name-input';
    const { organization: organizationInDatabase } = useOrganization();
    const { tab } = parseQueryParam();
    const text = useText();
    const { organization: initialOrganization, updateOrganization, orgId, orgRole, allOrganizationIds, switchOrganization, members } = useOrganization();
    const { user, getToken, refetchClaims, updateUser } = useUser();
    const [organization, setOrganization] = useState<Organization>(initialOrganization);
    const [newOrgLogo, setNewOrgLogo] = useState<File | null>(null);
    const storage = useStorage();
    const { back } = useQueryParam();
    const navigate = useNavigate();
    const location = useLocation();
    const [processing, setProcessing] = useState(false);
    // Store rules don't allow pointing to payments outside the store, so the native app has no billing tab
    const showBilling = !config.isNativeApp;
    const initialTab = tab === ORGANIZATION_SETTINGS_TABS.BILLING && !showBilling ? undefined : tab as ORGANIZATION_SETTINGS_TABS;
    const [activeTab, setActiveTab] = useState<ORGANIZATION_SETTINGS_TABS>(initialTab || ORGANIZATION_SETTINGS_TABS.DETAILS);
    const isOwner = orgRole === UserRole.ADMIN;

    const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
    const [creatingOrg, setCreatingOrg] = useState(false);
    const { register: registerNewOrg, handleSubmit: handleSubmitNewOrg, reset: resetNewOrgForm } = useForm<{ name: string }>();

    const deleteConfirmName = organizationInDatabase.name || orgId;
    const [ConfirmDeleteOrgModal, confirmDeleteOrg] = useConfirmModal(
        text.deleteOrganizationWarning,
        { requireTypedConfirmation: deleteConfirmName },
    );
    const [deletingOrg, setDeletingOrg] = useState(false);

    const [showTransferOwnershipModal, setShowTransferOwnershipModal] = useState(false);
    const [newOwnerEmail, setNewOwnerEmail] = useState('');
    const [transferringOwnership, setTransferringOwnership] = useState(false);
    const eligibleOwnershipMembers = members.filter(m => m.email && m.email !== user?.email);
    const [ConfirmTransferOwnershipModal, confirmTransferOwnership] = useConfirmModal(
        text.transferOwnershipWarning.replace('{email}', newOwnerEmail),
        { requireTypedConfirmation: newOwnerEmail },
    );

    const handleTabSelect = (newTab: string | null) => {
        const selectedTab = newTab as ORGANIZATION_SETTINGS_TABS;
        setActiveTab(selectedTab);
        const params = new URLSearchParams(location.search);
        params.set(QUERY_PARAMS.TAB, selectedTab);
        navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    };

    useEffect(() => {
        if (organizationInDatabase.name) {
            setWarnAboutMissingName(false);
        }
    }, [organizationInDatabase.name]);
    useEffect(() => setOrganization(initialOrganization),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [orgId])

    const handleMissingOrganizationName = () => {
        handleTabSelect(ORGANIZATION_SETTINGS_TABS.DETAILS);
        setWarnAboutMissingName(true);
        setTimeout(() => {
            const nameInput = document.getElementById(ORGANIZATION_NAME_INPUT_ID);
            if (!nameInput) return;
            nameInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            nameInput.focus();
        }, 0);
    };

    const handleUpdateSettings = async () => {
        setProcessing(true);
        try {
            let organizationUpdate: Partial<Organization> = {
                name: organization.name || '',
                email: organization.email || '',
                website: organization.website || '',
                features: organization.features || {},
                settings: organization.settings || {},
            };
            if (organization.place) {
                organizationUpdate.place = organization.place;
            }
            if (newOrgLogo) {
                const path = `${STORAGE_PATHS.ORGANIZATIONS}/${orgId}/${STORAGE_PATHS.PUBLIC}/${newOrgLogo.name}`;
                const storageRef = ref(storage, path);
                await uploadBytes(storageRef, newOrgLogo);
                const publicUrl = await getDownloadURL(storageRef);
                organizationUpdate.logo = publicUrl;
            }
            await updateOrganization(organizationUpdate);
            toast.success(text.defaultSuccess);
            if (back) {
                navigate(-1);
            }
        } catch (error) {
            console.error(error);
            toast.error(text.defaultError);
        }
        setProcessing(false);
    };

    const handleCreateOrganization = async (data: { name: string }) => {
        setCreatingOrg(true);
        try {
            const token = await getToken();
            const { orgId: newOrgId } = await createOrganizationApi(token, data.name);
            await refetchClaims();
            await switchOrganization(newOrgId);
            resetNewOrgForm();
            setShowCreateOrgModal(false);
            toast.success(text.organizationCreated);
            navigate('/');
        } catch (error) {
            console.error(error);
            toast.error(text.defaultError);
        }
        setCreatingOrg(false);
    };

    const handleDeleteOrganization = () => {
        confirmDeleteOrg(async () => {
            setDeletingOrg(true);
            try {
                const orgIdToDelete = orgId;
                const token = await getToken();
                const remainingOrgId = allOrganizationIds.find(id => id !== orgIdToDelete);

                // Switch away from the org being deleted BEFORE deleting it, so the still-mounted
                // OrganizationContext isn't listening to the doc's own deletion (which would
                // otherwise briefly strand the app on the loading screen).
                if (remainingOrgId) {
                    await switchOrganization(remainingOrgId);
                } else if (user) {
                    await updateUser({ settings: { ...user.settings, selectedOrganization: '' } });
                }

                await deleteOrganizationApi(token, orgIdToDelete);
                await refetchClaims();

                toast.success(text.organizationDeleted);
                window.location.href = '/';
            } catch (error) {
                console.error(error);
                toast.error(text.defaultError);
                setDeletingOrg(false);
            }
        });
    };

    const handleTransferOwnership = () => {
        if (!newOwnerEmail) return;

        setShowTransferOwnershipModal(false);
        confirmTransferOwnership(async () => {
            setTransferringOwnership(true);
            try {
                const token = await getToken();
                await transferOwnershipApi(token, orgId, newOwnerEmail);
                await refetchClaims();

                toast.success(text.ownershipTransferred);
                setNewOwnerEmail('');
            } catch (error) {
                console.error(error);
                toast.error(text.defaultError);
            }
            setTransferringOwnership(false);
        });
    };

    return (
        <>
            <PageHeader
                title={text.organizationSettings}
                description={text.organizationSettingsDescription}
                showBackButton={!!back}
                actions={activeTab !== ORGANIZATION_SETTINGS_TABS.BILLING ? [{
                    icon: 'bi-floppy',
                    text: text.save,
                    processing: processing,
                    onClick: handleUpdateSettings,
                }] : []}
            />
            <Tabs activeKey={activeTab} onSelect={handleTabSelect} className="mb-3">
                <Tab eventKey={ORGANIZATION_SETTINGS_TABS.DETAILS} title={text.details}>
                    <div className='d-flex flex-column align-items-start mt-2 gap-3 w-100'>
                        <div className='w-100'>
                            <label>{text.name}</label>
                            <p className='text-muted small m-0'>{text.organizationNameDescription}</p>
                            <FormControl id={ORGANIZATION_NAME_INPUT_ID} value={organization.name || ''}
                                isInvalid={warnAboutMissingName}
                                onChange={(e) => setOrganization({ ...organization, name: e.target.value })}
                            />
                            {warnAboutMissingName && (
                                <FormControl.Feedback type='invalid'>
                                    {text.organizationNameRequired}
                                </FormControl.Feedback>
                            )}
                        </div>
                        <div className='w-100'>
                            <label>{text.email}</label>
                            <p className='text-muted small m-0'>{text.organizationEmailDescription}</p>
                            <FormControl value={organization.email || ''}
                                type='email'
                                onChange={(e) => setOrganization({ ...organization, email: e.target.value })}
                            />
                        </div>
                        <div className='w-100'>
                            <label>{text.website}</label>
                            <p className='text-muted small m-0'>{text.organizationWebsiteDescription}</p>
                            <FormControl value={organization.website || ''}
                                type='url'
                                onChange={(e) => setOrganization({ ...organization, website: e.target.value })}
                            />
                        </div>
                        <div>
                            <label>{text.logo}</label>
                            <p className='text-muted small m-0'>{text.organizationLogoDescription}</p>
                            <OrganizationLogo
                                logo={organization.logo}
                                onNewFile={setNewOrgLogo}
                            />
                        </div>
                        <div className='w-100'>
                            <label>{text.currency}</label>
                            <p className='text-muted small m-0'>{text.currencyDescription}</p>
                            <FormSelect
                                value={organization.settings?.currency || ''}
                                className='w-unset'
                                onChange={(e) => setOrganization({
                                    ...organization,
                                    settings: { ...organization.settings, currency: e.target.value as OrgCurrency }
                                })}
                            >
                                <option value="SEK">SEK</option>
                                <option value="USD">USD</option>
                                <option value="CZK">CZK</option>
                                <option value="DKK">DKK</option>
                                <option value="EUR">EUR</option>
                            </FormSelect>
                        </div>
                        <div className='w-100'>
                            <label>{text.address}</label>
                            <AddressSearch
                                placeHolder={text.headquarterDescription}
                                place={organization.place}
                                onPlaceChange={async (place) => setOrganization({ ...organization, place })}
                                noPointOnMap
                            />
                        </div>
                        <div className='w-100'>
                            <label>{text.paymentInstructions}</label>
                            <p className='text-muted small m-0'>{text.paymentInstructionsDescription}</p>
                            <FormControl
                                as='textarea'
                                rows={3}
                                value={organization.settings?.paymentInstructions || ''}
                                onChange={(e) => setOrganization({
                                    ...organization,
                                    settings: { ...organization.settings, paymentInstructions: e.target.value }
                                })}
                            />
                        </div>
                    </div>
                </Tab>
                <Tab eventKey={ORGANIZATION_SETTINGS_TABS.TEAM} title={text.team}>
                    <TeamManagement onMissingOrganizationName={handleMissingOrganizationName} />
                </Tab>
                <Tab eventKey={ORGANIZATION_SETTINGS_TABS.FEATURES} title={text.features}>
                    <Form className="d-flex flex-column gap-2">
                        <FormCheck
                            type="switch"
                            id="hours-tracking-switch"
                            label={text.enableHoursTracking}
                            checked={!!organization.features?.hoursTracking}
                            onChange={e => setOrganization({
                                ...organization,
                                features: {
                                    ...organization.features,
                                    hoursTracking: e.target.checked
                                }
                            })}
                        />
                        <FormCheck
                            type="switch"
                            id="risk-assessment-switch"
                            label={text.enableRiskAssessment}
                            checked={!!organization.features?.riskAssessment}
                            onChange={e => setOrganization({
                                ...organization,
                                features: {
                                    ...organization.features,
                                    riskAssessment: e.target.checked
                                }
                            })}
                        />
                    </Form>
                </Tab>
                {showBilling && (
                    <Tab eventKey={ORGANIZATION_SETTINGS_TABS.BILLING} title={text.billing}>
                        <BillingTab />
                    </Tab>
                )}
                <Tab eventKey={ORGANIZATION_SETTINGS_TABS.DANGER_ZONE} title={text.dangerZone}>
                    <div className='d-flex flex-column gap-4 mt-2'>
                        <div className='d-flex justify-content-between align-items-start border rounded p-3'>
                            <div>
                                <label className='m-0'>{text.createNewOrganization}</label>
                                <p className='m-0 text-muted small'>{text.createNewOrganizationDescription}</p>
                            </div>
                            <AdvancedButton variant='secondary' icon='bi-plus' onClick={() => setShowCreateOrgModal(true)}>
                                {text.createNewOrganization}
                            </AdvancedButton>
                        </div>

                        <div className='d-flex justify-content-between align-items-start border rounded p-3'>
                            <div>
                                <label className='m-0'>{text.transferOwnership}</label>
                                <p className='m-0 text-muted small'>{text.transferOwnershipDescription}</p>
                                {!isOwner && <p className='m-0 text-danger small'>{text.transferOwnershipOwnerOnly}</p>}
                            </div>
                            <AdvancedButton
                                variant='secondary'
                                icon='bi-arrow-left-right'
                                disabled={!isOwner}
                                processing={transferringOwnership}
                                onClick={() => setShowTransferOwnershipModal(true)}
                            >
                                {text.transferOwnership}
                            </AdvancedButton>
                        </div>

                        <div className='d-flex justify-content-between align-items-start border border-danger-subtle rounded p-3'>
                            <div>
                                <label className='m-0'>{text.deleteOrganization}</label>
                                <p className='m-0 text-muted small'>{text.deleteOrganizationDescription}</p>
                                {!isOwner && <p className='m-0 text-danger small'>{text.deleteOrganizationOwnerOnly}</p>}
                            </div>
                            <AdvancedButton
                                variant='danger'
                                icon='bi-trash'
                                disabled={!isOwner}
                                processing={deletingOrg}
                                onClick={handleDeleteOrganization}
                            >
                                {text.deleteOrganization}
                            </AdvancedButton>
                        </div>
                    </div>
                </Tab>
            </Tabs>
            {activeTab !== ORGANIZATION_SETTINGS_TABS.BILLING && activeTab !== ORGANIZATION_SETTINGS_TABS.DANGER_ZONE && (
                <div className='d-flex justify-content-end'>
                    <AdvancedButton icon="bi-floppy" processing={processing} onClick={handleUpdateSettings} >
                        {text.save}
                    </AdvancedButton>
                </div>
            )}

            <Modal show={showCreateOrgModal} onHide={() => setShowCreateOrgModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{text.createNewOrganization}</Modal.Title>
                </Modal.Header>
                <form onSubmit={handleSubmitNewOrg(handleCreateOrganization)}>
                    <Modal.Body>
                        <Form.Group>
                            <Form.Label>{text.name}</Form.Label>
                            <FormControl {...registerNewOrg('name', { required: true })} autoFocus />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <AdvancedButton variant='outline-secondary' onClick={() => setShowCreateOrgModal(false)}>
                            {text.cancel}
                        </AdvancedButton>
                        <AdvancedButton variant='primary' type='submit' processing={creatingOrg}>
                            {text.create}
                        </AdvancedButton>
                    </Modal.Footer>
                </form>
            </Modal>
            <ConfirmDeleteOrgModal />

            <Modal show={showTransferOwnershipModal} onHide={() => { setShowTransferOwnershipModal(false); setNewOwnerEmail(''); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{text.transferOwnershipModalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body className='d-flex flex-column gap-3'>
                    <div className='text-muted small'>{text.transferOwnershipDescription}</div>
                    {eligibleOwnershipMembers.length === 0 ? (
                        <div className='text-muted small'>{text.transferOwnershipNoEligibleMembers}</div>
                    ) : (
                        <OrgMemberSelect
                            label={text.transferOwnershipSelectMember}
                            members={eligibleOwnershipMembers}
                            value={newOwnerEmail}
                            onChange={setNewOwnerEmail}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <AdvancedButton variant='outline-secondary' onClick={() => { setShowTransferOwnershipModal(false); setNewOwnerEmail(''); }}>
                        {text.cancel}
                    </AdvancedButton>
                    <AdvancedButton variant='danger' disabled={!newOwnerEmail} onClick={handleTransferOwnership}>
                        {text.transferOwnership}
                    </AdvancedButton>
                </Modal.Footer>
            </Modal>
            <ConfirmTransferOwnershipModal />
        </>
    );
}
